import { useState } from "react";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const BENCHMARKS: Record<string, number> = { U11: 14.0, U13: 16.1, U15: 17.6, U17: 18.5 };

const MOCK_FITNESS = [
  { name: "Quinn F",     academy: "Northside Cricket", ageGroup: "U13", position: "Batting All-rounder",  yoyoScore: 17.2 },
  { name: "Ansh S",     academy: "Westfield Cricket",  ageGroup: "U13", position: "Bowling All-rounder",  yoyoScore: 16.8 },
  { name: "Jordan L",   academy: "Eastside Academy",   ageGroup: "U13", position: "Fast Bowler",          yoyoScore: 15.9 },
  { name: "Nirvaan P",  academy: "Northside Cricket",  ageGroup: "U13", position: "Wicket-keeper",        yoyoScore: 14.2 },
  { name: "Daron W",    academy: "Eastside Academy",   ageGroup: "U13", position: "Batting All-rounder",  yoyoScore: 16.5 },
  { name: "Marcus J",   academy: "Central Sports",     ageGroup: "U13", position: "Spin Bowler",          yoyoScore: 16.1 },
  { name: "Aryan S",    academy: "Northside Cricket",  ageGroup: "U11", position: "Batting All-rounder",  yoyoScore: 15.2 },
  { name: "Rohan T",    academy: "Westfield Cricket",  ageGroup: "U11", position: "Fast Bowler",          yoyoScore: 13.8 },
  { name: "Kiran B",    academy: "Central Sports",     ageGroup: "U11", position: "Wicket-keeper",        yoyoScore: 12.5 },
  { name: "Shane M",    academy: "Eastside Academy",   ageGroup: "U15", position: "Batting All-rounder",  yoyoScore: 18.1 },
  { name: "Brad T",     academy: "Eastside Academy",   ageGroup: "U15", position: "Fast Bowler",          yoyoScore: 17.4 },
];

type Status = "pass" | "borderline" | "fail";

function getStatus(score: number, ageGroup: string): Status {
  const benchmark = BENCHMARKS[ageGroup] ?? 16.0;
  if (score >= benchmark) return "pass";
  if (score >= benchmark - 0.5) return "borderline";
  return "fail";
}

const STATUS_STYLE: Record<Status, { badge: string; row: string; bar: string }> = {
  pass:       { badge: "bg-green-100 text-green-700 border-green-300", row: "",          bar: "bg-green-500" },
  borderline: { badge: "bg-amber-100 text-amber-700 border-amber-300", row: "bg-amber-50", bar: "bg-amber-400" },
  fail:       { badge: "bg-red-100   text-red-700   border-red-300",   row: "bg-red-50",   bar: "bg-red-500"   },
};

const AGE_GROUPS = ["All", "U11", "U13", "U15", "U17"] as const;
type AgeFilter = (typeof AGE_GROUPS)[number];

export default function HubFitnessTestsDemo() {
  const [ageFilter, setAgeFilter] = useState<AgeFilter>("All");

  const players = MOCK_FITNESS.map((p) => ({ ...p, status: getStatus(p.yoyoScore, p.ageGroup) }));
  const filtered = ageFilter === "All" ? players : players.filter((p) => p.ageGroup === ageFilter);

  const passCount       = players.filter((p) => p.status === "pass").length;
  const borderlineCount = players.filter((p) => p.status === "borderline").length;
  const failCount       = players.filter((p) => p.status === "fail").length;

  const benchmark = ageFilter !== "All" ? BENCHMARKS[ageFilter] : null;

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-fitness-tests-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold text-gray-900">Fitness Tests</h1>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3" data-testid="summary-bar">
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-green-700" data-testid="count-pass">{passCount}</p>
            <p className="text-xs text-green-600 mt-0.5">Passed</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-amber-700" data-testid="count-borderline">{borderlineCount}</p>
            <p className="text-xs text-amber-600 mt-0.5">Borderline</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-red-700" data-testid="count-fail">{failCount}</p>
            <p className="text-xs text-red-600 mt-0.5">Failed</p>
          </div>
        </div>

        {/* Age filter */}
        <div className="flex flex-wrap gap-2" data-testid="age-filter-pills">
          {AGE_GROUPS.map((ag) => (
            <button
              key={ag}
              onClick={() => setAgeFilter(ag)}
              data-testid={`age-pill-${ag.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                ageFilter === ag
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {ag}
            </button>
          ))}
        </div>

        {/* Benchmark callout — only when single age group selected */}
        {benchmark !== null && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 font-medium" data-testid="benchmark-callout">
            {ageFilter} Benchmark: Yo-Yo Level {benchmark.toFixed(1)}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="fitness-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Academy</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Position</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Yo-Yo Score</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Benchmark</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((player) => {
                  const style = STATUS_STYLE[player.status];
                  const barPct = Math.min(100, (player.yoyoScore / 20) * 100);
                  const bench = BENCHMARKS[player.ageGroup] ?? 16.0;
                  const needsRetest = player.status === "fail" || player.status === "borderline";
                  return (
                    <tr
                      key={`${player.name}-${player.ageGroup}`}
                      className={style.row}
                      data-testid={`fitness-row-${player.status}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{player.name}</td>
                      <td className="px-4 py-3 text-gray-600">{player.academy}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{player.position}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-800 w-8 shrink-0">{player.yoyoScore.toFixed(1)}</span>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[60px]">
                            <div
                              className={`h-full rounded-full ${style.bar}`}
                              style={{ width: `${barPct}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{bench.toFixed(1)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`border text-xs ${style.badge}`}>{player.status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {needsRetest ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={() => { /* Retest scheduling — Phase 1 */ }}
                            data-testid={`btn-retest-${player.name.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            Schedule Retest
                          </Button>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info box */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="info-box">
          Yo-Yo fitness test is a mandatory gate for Phase 2 → Regional advancement. Players with Fail or
          Borderline status must retest before selection scoring begins.
        </div>
      </div>
    </HubAdminDemoLayout>
  );
}
